import type { QueryClient } from "@tanstack/react-query";

import { formatGoalDates } from "../../../pages/Goals/Company/SubPages/GoalsList/components/filterDataFetchers";

import { SERVICE_GOAL, getSearch } from "../../../api/services/defs/search-service";
import { Goal } from "../../../api/services/searchServiceResultTypes/goal";

export type GoalAlignmentValue = {
	id: string;
	name: string;
	status: string;
	progress: number;
	// Optional fields - not always available from all data sources
	color?: string;
	assignee?: string;
	created?: string;
	due?: string;
	target?: number;
	goalValue?: number;
	categories?: string[];
};

export type GoalAlignmentFilterMode = "public-only" | "all-accessible";

export type GoalAlignmentFilterConfig = {
	filterMode: GoalAlignmentFilterMode;
	currentUserId?: string;
};

export const createFetchGoalsForAlignment = (queryClient: QueryClient, filterConfig?: GoalAlignmentFilterConfig) => {
	return async (searchTerm: string): Promise<GoalAlignmentValue[]> => {
		const cacheKey = [
			"selectGoalAlignmentFilter",
			searchTerm,
			filterConfig?.filterMode,
			filterConfig?.currentUserId,
		];
		const cachedData = queryClient.getQueryData<GoalAlignmentValue[]>(cacheKey);
		if (cachedData) {
			return cachedData;
		}

		const filters: Record<string, any> = {
			status_in: ["active"],
			assignee_name_or_category_name_or_goal_name_re: searchTerm,
		};

		if (filterConfig?.filterMode === "public-only") {
			filters.transparency_in = ["public"];
		}

		const response = await getSearch<Goal>(SERVICE_GOAL, filters, 0, 20, { name: "name", direction: "asc" });

		const goals = response?.results || [];
		const formattedGoals = goals.map((goal) => {
			const formattedStatus = goal.status ? goal.status.charAt(0).toUpperCase() + goal.status.slice(1) : "";
			const createdAt = formatGoalDates(goal.createdAt);
			const endDate = formatGoalDates(goal.endDate);

			return {
				id: goal._id,
				name: goal.name,
				status: formattedStatus,
				color: goal.outlook?.color || "#222",
				assignee: goal.assignee?.name || "",
				created: createdAt,
				due: endDate,
				target: goal.target || 0,
				goalValue: goal.value || 0,
				progress: goal.value && goal.target ? (goal.value / goal.target) * 100 : 0,
				categories: goal.categories?.map((category) => category.name) || [],
			};
		});

		queryClient.setQueryData(cacheKey, formattedGoals);

		return formattedGoals;
	};
};
